import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  Chip,
} from '@mui/material';
import {
  ExitToApp,
  TableChart,
  ManageSearch,
  Person,
  Email,
  AdminPanelSettings,
  HelpOutline,
  KeyboardArrowDown,
  Business,
  Visibility,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { profileAPI } from '../services/api';
import AISettingsModal from './AISettingsModal';

interface HeaderProps {
  onBatchClick?: () => void;
  onViewsClick?: () => void;
  aiTestProfileData?: Record<string, unknown>;
}

const Header: React.FC<HeaderProps> = ({ onBatchClick, onViewsClick, aiTestProfileData }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [orgName, setOrgName] = useState<string | null>(null);
  const open = Boolean(anchorEl);
  const [showAISettings, setShowAISettings] = useState(false);
  const isAiAdmin = !!(user as any)?.is_ai_admin;
  const isSuperAdmin = (user as any)?.role === 'superadmin';

  // Fetch org name once when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    profileAPI.getUsageStats()
      .then(data => {
        if (data?.organization_name) setOrgName(data.organization_name);
      })
      .catch(() => {});
  }, [isAuthenticated]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const handleBatchSearch = () => {
    handleClose();
    onBatchClick?.();
  };

  const handleMyViews = () => {
    handleClose();
    onViewsClick?.();
  };

  const handleRecentSearches = () => {
    handleClose();
    window.dispatchEvent(new CustomEvent('kc:openRecentSearches'));
  };

  const handleProfileSettings = () => {
    handleClose();
    window.dispatchEvent(new CustomEvent('kc:openProfileSettings'));
  };

  const handleAISettings = () => {
    handleClose();
    setShowAISettings(true);
  };

  // Generate initials avatar letters
  const initials = [user?.first_name?.[0], user?.last_name?.[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase() || '?';

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AppBar position="fixed" sx={{ mb: 3, backgroundColor: '#283E56', zIndex: 1300 }}>
      <Toolbar>
        {/* ── Logo ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': { opacity: 0.8, transform: 'scale(1.02)', transition: 'all 0.2s ease' },
            }}
            onClick={() => window.location.reload()}
          >
            <img
              src="/knowledgecore_searchbanner.jpeg"
              alt="Knowledge Core Logo"
              style={{ height: '40px', marginRight: '15px' }}
            />
            <Typography variant="h6" component="div">
              Knowledge Core IQ Search
            </Typography>
          </Box>
        </Box>

        {/* ── Profile trigger ── */}
        <Box
          onClick={handleMenu}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            border: '1px solid rgba(255,255,255,0.15)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.08)',
              borderColor: 'rgba(255,255,255,0.35)',
            },
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: '#00C853',
              fontSize: '0.8rem',
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'white' }}>
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.65, lineHeight: 1, color: 'white' }}>
              {user?.email}
            </Typography>
            {orgName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                <Business sx={{ fontSize: 13, opacity: 0.75, color: '#00C853' }} />
                <Typography variant="caption" sx={{ opacity: 0.65, lineHeight: 1, color: 'white', fontSize: '0.7rem' }}>
                  {orgName}
                </Typography>
              </Box>
            )}
          </Box>
          <KeyboardArrowDown
            sx={{
              fontSize: 18,
              opacity: 0.7,
              transition: 'transform 0.2s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>

        {/* ── Dropdown menu ── */}
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          onClick={handleClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              elevation: 4,
              sx: {
                mt: 1,
                minWidth: 260,
                borderRadius: 2,
                overflow: 'visible',
                border: '1px solid rgba(0,0,0,0.08)',
                '&::before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: -6,
                  right: 20,
                  width: 12,
                  height: 12,
                  backgroundColor: 'white',
                  transform: 'rotate(45deg)',
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderBottom: 'none',
                  borderRight: 'none',
                },
              },
            },
          }}
        >
          {/* ── User info header ── */}
          <Box sx={{ px: 2, py: 1.5, pointerEvents: 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 40, height: 40, backgroundColor: '#283E56', fontWeight: 700 }}>
                {initials}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#283E56' }}>
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Email sx={{ fontSize: 11 }} />
                  {user?.email}
                </Typography>
              </Box>
            </Box>
            <Chip
              label="Active User"
              size="small"
              sx={{
                mt: 1,
                height: 20,
                fontSize: '0.68rem',
                backgroundColor: '#dcfce7',
                color: '#166534',
                fontWeight: 600,
              }}
            />
          </Box>

          <Divider />

          {/* ── My Account ── */}
          <Box sx={{ px: 1, pt: 0.5 }}>
            <Typography variant="caption" sx={{ px: 1, color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              My Account
            </Typography>
          </Box>

          <MenuItem
            onClick={handleProfileSettings}
            sx={{ mx: 1, borderRadius: 1, '&:hover': { backgroundColor: 'rgba(40,62,86,0.06)' } }}
          >
            <ListItemIcon><Person fontSize="small" sx={{ color: '#283E56' }} /></ListItemIcon>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Profile Settings</Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Update your name, password & preferences</Typography>
            </Box>
          </MenuItem>

          <MenuItem
            onClick={handleClose}
            sx={{ mx: 1, borderRadius: 1, '&:hover': { backgroundColor: 'rgba(40,62,86,0.06)' } }}
          >
            <ListItemIcon><AdminPanelSettings fontSize="small" sx={{ color: '#283E56' }} /></ListItemIcon>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Account Security</Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Session & access settings</Typography>
            </Box>
          </MenuItem>

          <Divider sx={{ my: 0.5 }} />

          {/* ── Tools ── */}
          <Box sx={{ px: 1 }}>
            <Typography variant="caption" sx={{ px: 1, color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Tools
            </Typography>
          </Box>

          <MenuItem
            onClick={handleRecentSearches}
            sx={{ mx: 1, borderRadius: 1, '&:hover': { backgroundColor: 'rgba(40,62,86,0.06)' } }}
          >
            <ListItemIcon><ManageSearch fontSize="small" sx={{ color: '#283E56' }} /></ListItemIcon>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Recent Searches</Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>View your search history</Typography>
            </Box>
          </MenuItem>

          <MenuItem
            onClick={handleBatchSearch}
            sx={{ mx: 1, borderRadius: 1, '&:hover': { backgroundColor: 'rgba(40,62,86,0.06)' } }}
          >
            <ListItemIcon><TableChart fontSize="small" sx={{ color: '#283E56' }} /></ListItemIcon>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>Batch Search</Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Upload CSV & screen prospects</Typography>
            </Box>
          </MenuItem>

          {isAiAdmin && (
            <MenuItem
              onClick={handleAISettings}
              sx={{ mx: 1, borderRadius: 1, '&:hover': { backgroundColor: 'rgba(40,62,86,0.06)' } }}
            >
              <ListItemIcon><AdminPanelSettings fontSize="small" sx={{ color: '#283E56' }} /></ListItemIcon>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>AI Settings</Typography>
                <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Prompts, model & cache</Typography>
              </Box>
            </MenuItem>
          )}

          <MenuItem
            onClick={handleMyViews}
            sx={{ mx: 1, borderRadius: 1, '&:hover': { backgroundColor: 'rgba(40,62,86,0.06)' } }}
          >
            <ListItemIcon><Visibility fontSize="small" sx={{ color: '#283E56' }} /></ListItemIcon>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>My Views</Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>Saved prospect lists &amp; enrichment</Typography>
            </Box>
          </MenuItem>

          <Divider sx={{ my: 0.5 }} />

          {/* ── Support ── */}
          <MenuItem
            onClick={handleClose}
            sx={{ mx: 1, borderRadius: 1, '&:hover': { backgroundColor: 'rgba(40,62,86,0.06)' } }}
          >
            <ListItemIcon><HelpOutline fontSize="small" sx={{ color: '#6B7280' }} /></ListItemIcon>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>Help & Documentation</Typography>
          </MenuItem>

          <Divider sx={{ my: 0.5 }} />

          {/* ── Sign out ── */}
          <MenuItem
            onClick={handleLogout}
            sx={{
              mx: 1,
              mb: 0.5,
              borderRadius: 1,
              color: '#EF4444',
              '&:hover': { backgroundColor: 'rgba(239,68,68,0.06)' },
            }}
          >
            <ListItemIcon><ExitToApp fontSize="small" sx={{ color: '#EF4444' }} /></ListItemIcon>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#EF4444' }}>Sign Out</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
      <AISettingsModal
        open={showAISettings}
        onClose={() => setShowAISettings(false)}
        isSuperAdmin={isSuperAdmin}
        currentSearchProfile={aiTestProfileData}
      />
    </AppBar>
  );
};

export default Header;